import classNames from 'classnames';
import { Fragment, ReactNode } from 'react';
import styles from './TimetableVersionsOverview.module.scss';

/** Einrückung pro Baum-Ebene in px. */
const TREE_UNIT = 16;

type VersionTreeCellProps = {
  /** Baum-Linien je Einrückungsebene (siehe VersionTreeRow). Leeres Array = Wurzelknoten. */
  prefix: boolean[];
  /** Hat diese Version Kind-Versionen in der Liste? Steuert die nach unten weiterführende Linie. */
  hasChildren: boolean;
  /** Beschriftung der Zelle (i. d. R. die Versionsnummer). */
  children: ReactNode;
};

/**
 * Erste Tabellen-Zelle einer Versionszeile inkl. der Baum-Verbinder, die Eltern- und
 * Kind-Versionen visuell verknüpfen. Die Linien werden absolut innerhalb der (auf volle
 * Zeilenhöhe gestreckten) Zelle positioniert.
 */
export const VersionTreeCell = ({ prefix, hasChildren, children }: VersionTreeCellProps) => {
  const depth = prefix.length;

  return (
    <div className={styles.version}>
      {prefix.map((hasNextSibling, index) => {
        const left = index * TREE_UNIT + TREE_UNIT / 2;
        const isConnector = index === depth - 1;

        if (!isConnector) {
          // Durchgezogene vertikale Linie für Vorfahren, die noch weitere Geschwister haben.
          return hasNextSibling ? (
            <span
              key={index}
              aria-hidden
              className={classNames(styles['tree-line'], styles['tree-vertical'])}
              style={{ left }}
            />
          ) : null;
        }

        // Eckverbinder zur eigenen Zeile: durchgezogen (├) wenn noch Geschwister folgen, sonst Ecke (└).
        return (
          <Fragment key={index}>
            <span
              aria-hidden
              className={classNames(
                styles['tree-line'],
                hasNextSibling ? styles['tree-vertical'] : styles['tree-corner'],
              )}
              style={{ left }}
            />
            <span
              aria-hidden
              className={classNames(styles['tree-line'], styles['tree-horizontal'])}
              style={{ left, width: TREE_UNIT / 2 + 6 }}
            />
          </Fragment>
        );
      })}
      {hasChildren && (
        <span
          aria-hidden
          className={classNames(styles['tree-line'], styles['tree-parent-stub'])}
          style={{ left: depth * TREE_UNIT + TREE_UNIT / 2 }}
        />
      )}
      <span style={{ paddingLeft: depth * TREE_UNIT + 14 }}>{children}</span>
    </div>
  );
};
